/**
 * SceneCard — card de uma cena na timeline do modal de gerar vídeo.
 *
 * Renderiza UI específica por tipo:
 *   - fala: textarea + emotion + speed + pitch + avatarStyle
 *   - pausa: slider de duração
 *   - broll: prompt + duração + thumb + botões [Gerar] [Upload] [Pular]
 *
 * Botões up/down/delete na lateral. Justificativa do diretor (razao)
 * mostrada como nota em itálico se existe.
 */
import { useRef, useState } from 'react';
import {
  ArrowUp, ArrowDown, Trash2, Mic, Pause, Image as ImageIcon,
  Loader2, Wand2, Upload, MinusCircle, AlertCircle,
} from 'lucide-react';
import { api, ApiError } from '../lib/api';
import {
  type Cena, type CenaFala, type CenaPausa, type CenaBroll,
  EMOTIONS,
} from '../lib/cena';

interface Props {
  cena: Cena;
  index: number;
  total: number;
  aspectRatio: '16:9' | '9:16' | '1:1';
  onChange: (cena: Cena) => void;
  onDelete: () => void;
  onMove: (delta: -1 | 1) => void;
}

export default function SceneCard(props: Props) {
  const { cena } = props;
  const cor =
    cena.tipo === 'fala' ? 'border-purple-300 dark:border-purple-700'
    : cena.tipo === 'pausa' ? 'border-gray-300 dark:border-gray-600'
    : 'border-amber-300 dark:border-amber-700';

  return (
    <div className={`border-l-4 ${cor} bg-white dark:bg-gray-800 rounded-r-md shadow-sm`}>
      <div className="flex items-stretch">
        <div className="flex flex-col items-center justify-center px-2 py-3 gap-1 border-r border-gray-100 dark:border-gray-700">
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {props.index + 1}
          </span>
          <button
            onClick={() => props.onMove(-1)}
            disabled={props.index === 0}
            title="Mover acima"
            className="text-gray-400 hover:text-purple-600 disabled:opacity-30"
          >
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => props.onMove(1)}
            disabled={props.index === props.total - 1}
            title="Mover abaixo"
            className="text-gray-400 hover:text-purple-600 disabled:opacity-30"
          >
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={props.onDelete}
            title="Remover"
            className="text-gray-400 hover:text-red-600 mt-2"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex-1 p-3 space-y-2">
          {cena.tipo === 'fala' && (
            <FalaEditor cena={cena} onChange={props.onChange} />
          )}
          {cena.tipo === 'pausa' && (
            <PausaEditor cena={cena} onChange={props.onChange} />
          )}
          {cena.tipo === 'broll' && (
            <BrollEditor cena={cena} aspectRatio={props.aspectRatio} onChange={props.onChange} />
          )}
          {cena.razao && (
            <p className="text-xs italic text-gray-500 dark:text-gray-400">
              💡 {cena.razao}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FalaEditor({ cena, onChange }: { cena: CenaFala; onChange: (c: Cena) => void }) {
  return (
    <>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-purple-700 dark:text-purple-400 font-semibold">
        <Mic className="w-3.5 h-3.5" /> Fala
      </div>
      <textarea
        value={cena.texto}
        onChange={(e) => onChange({ ...cena, texto: e.target.value })}
        rows={2}
        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
        placeholder="Texto que o avatar vai falar..."
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
        <label className="flex flex-col gap-1">
          <span className="text-gray-500 dark:text-gray-400">Emoção</span>
          <select
            value={cena.emotion ?? 'Friendly'}
            onChange={(e) => onChange({ ...cena, emotion: e.target.value as any })}
            className="px-2 py-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded"
          >
            {EMOTIONS.map((em) => <option key={em} value={em}>{em}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500 dark:text-gray-400">Velocidade {(cena.speed ?? 1).toFixed(2)}x</span>
          <input
            type="range" min="0.5" max="1.5" step="0.05"
            value={cena.speed ?? 1}
            onChange={(e) => onChange({ ...cena, speed: parseFloat(e.target.value) })}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-gray-500 dark:text-gray-400">Pitch {cena.pitch ?? 0}</span>
          <input
            type="range" min="-50" max="50" step="1"
            value={cena.pitch ?? 0}
            onChange={(e) => onChange({ ...cena, pitch: parseInt(e.target.value) })}
          />
        </label>
      </div>
    </>
  );
}

function PausaEditor({ cena, onChange }: { cena: CenaPausa; onChange: (c: Cena) => void }) {
  return (
    <>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-gray-700 dark:text-gray-400 font-semibold">
        <Pause className="w-3.5 h-3.5" /> Pausa
      </div>
      <label className="flex items-center gap-3 text-sm">
        <span className="text-gray-600 dark:text-gray-300 w-20">Duração</span>
        <input
          type="range" min="0.5" max="5" step="0.1"
          value={cena.duracao}
          onChange={(e) => onChange({ ...cena, duracao: parseFloat(e.target.value) })}
          className="flex-1"
        />
        <span className="text-gray-700 dark:text-gray-200 w-12 text-right">
          {cena.duracao.toFixed(1)}s
        </span>
      </label>
    </>
  );
}

function BrollEditor({
  cena, aspectRatio, onChange,
}: { cena: CenaBroll; aspectRatio: '16:9' | '9:16' | '1:1'; onChange: (c: Cena) => void }) {
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function gerar() {
    if (!cena.imagemPrompt?.trim()) {
      setErro('Escreva um prompt antes de gerar');
      return;
    }
    setGerando(true);
    setErro(null);
    try {
      const r = await api.post<{ url: string; assetId: string; custoUsd: number }>('/videos/imagem', {
        prompt: cena.imagemPrompt,
        aspectRatio,
        comUpload: true,
      });
      onChange({ ...cena, imagemUrl: r.url, imagemAssetId: r.assetId, fonte: 'ia' });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha ao gerar imagem');
    } finally {
      setGerando(false);
    }
  }

  async function fazerUpload(file: File) {
    if (file.size > 10 * 1024 * 1024) {
      setErro('Imagem maior que 10MB');
      return;
    }
    setGerando(true);
    setErro(null);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result as string);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      const r = await api.post<{ assetId: string }>('/videos/imagem/upload', { dataUrl });
      onChange({ ...cena, imagemUrl: dataUrl, imagemAssetId: r.assetId, fonte: 'upload' });
    } catch (e) {
      setErro(e instanceof ApiError ? e.message : 'Falha no upload');
    } finally {
      setGerando(false);
    }
  }

  return (
    <>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 font-semibold">
        <ImageIcon className="w-3.5 h-3.5" /> B-Roll
      </div>
      <textarea
        value={cena.imagemPrompt ?? ''}
        onChange={(e) => onChange({ ...cena, imagemPrompt: e.target.value })}
        rows={2}
        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 rounded text-sm"
        placeholder="Descreva a imagem (prompt para DALL-E)..."
      />
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-gray-600 dark:text-gray-300">Duração</span>
          <input
            type="range" min="1" max="10" step="0.5"
            value={cena.duracao}
            onChange={(e) => onChange({ ...cena, duracao: parseFloat(e.target.value) })}
            className="w-32"
          />
          <span className="text-gray-700 dark:text-gray-200 w-10 text-right text-xs">
            {cena.duracao}s
          </span>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={gerar}
          disabled={gerando}
          className="px-2 py-1 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded disabled:opacity-50 flex items-center gap-1"
        >
          {gerando ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
          Gerar IA <span className="opacity-70">($0.04)</span>
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={gerando}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
        >
          <Upload className="w-3 h-3" /> Upload
        </button>
        <button
          onClick={() => onChange({ ...cena, fonte: 'pular', imagemUrl: undefined, imagemAssetId: undefined })}
          className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
        >
          <MinusCircle className="w-3 h-3" /> Pular
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) fazerUpload(f);
          }}
        />
        {cena.imagemUrl && (
          <img
            src={cena.imagemUrl}
            alt="preview"
            className="w-12 h-12 object-cover rounded border border-gray-200 dark:border-gray-700"
          />
        )}
      </div>
      {erro && (
        <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" /> {erro}
        </p>
      )}
    </>
  );
}
